import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api, apiError, eur } from "@/lib/apiClient";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Wallet, Loader2, FileText, CheckCircle2, BadgeEuro } from "lucide-react";

const PRODUCT_NAMES = {
  rc_economia_solidale: "RC Economia Solidale",
  mrv_volontariato: "Multirischi Volontariato",
  infortuni_economia_solidale: "Infortuni Economia Solidale",
  cat_nat_impresa: "CAT NAT Assimoco",
  multirischi_beni: "Multirischi Danni ai Beni",
};
const STATO = {
  maturata: { label: "Maturata", cls: "bg-amber-100 text-amber-800" },
  fatturata: { label: "Fatturata", cls: "bg-forest/10 text-forest" },
  liquidata: { label: "Liquidata", cls: "bg-emerald-100 text-emerald-700" },
};

export default function Provvigioni() {
  const { user } = useAuth();
  const isAdmin = user?.role && ["agency_admin", "agency_operator"].includes(user.role);
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState("");

  const load = useCallback(() => api.get("/provvigioni").then(({ data }) => setRows(data)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);

  const totals = useMemo(() => {
    const t = { maturata: 0, fatturata: 0, liquidata: 0 };
    rows.forEach((r) => { if (r.lordo != null && t[r.stato] != null) t[r.stato] += r.lordo; });
    return t;
  }, [rows]);

  const segnaFattura = (r) => {
    setBusy(r.id);
    api.post(`/quotes/${r.id}/provvigione/fattura`).then(() => { toast.success("Fattura segnalata all'Agenzia"); load(); })
      .catch((e) => toast.error(apiError(e))).finally(() => setBusy(""));
  };
  const liquida = (r) => {
    setBusy(r.id);
    api.post(`/quotes/${r.id}/provvigione/liquida`).then(() => { toast.success("Provvigione liquidata"); load(); })
      .catch((e) => toast.error(apiError(e))).finally(() => setBusy(""));
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-heading text-3xl font-semibold text-forest">Estratto conto provvigioni</h1>
        <p className="mt-1 text-muted-foreground">
          {isAdmin
            ? "Registra il lordo maturato per ogni polizza incassata e liquida le provvigioni a fronte della fattura del Partner."
            : "Provvigioni lordo maturate sulle polizze emesse e incassate. Emetti fattura il 1° del mese: l'Agenzia bonifica l'importo (nessun passaggio di premi)."}
        </p>
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        <Card className="flex items-center gap-3 rounded-xl border-border/60 p-4" data-testid="tot-maturata">
          <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-amber-50 text-amber-600"><Wallet className="h-5 w-5" /></div>
          <div><div className="font-heading text-2xl font-semibold text-forest">{eur(totals.maturata)}</div><div className="text-xs text-muted-foreground">Da fatturare</div></div>
        </Card>
        <Card className="flex items-center gap-3 rounded-xl border-border/60 p-4" data-testid="tot-fatturata">
          <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-sage text-forest"><FileText className="h-5 w-5" /></div>
          <div><div className="font-heading text-2xl font-semibold text-forest">{eur(totals.fatturata)}</div><div className="text-xs text-muted-foreground">Fatturate — in attesa bonifico</div></div>
        </Card>
        <Card className="flex items-center gap-3 rounded-xl border-border/60 p-4" data-testid="tot-liquidata">
          <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-emerald-50 text-emerald-600"><CheckCircle2 className="h-5 w-5" /></div>
          <div><div className="font-heading text-2xl font-semibold text-forest">{eur(totals.liquidata)}</div><div className="text-xs text-muted-foreground">Liquidate</div></div>
        </Card>
      </div>

      <Card className="overflow-hidden rounded-xl border-border/60">
        <Table>
          <TableHeader><TableRow className="bg-sage/50">
            <TableHead>Pratica</TableHead>{isAdmin && <TableHead>Partner</TableHead>}
            <TableHead>Contraente</TableHead><TableHead>N. proposta</TableHead>
            <TableHead>Mese</TableHead><TableHead className="text-right">Lordo provv.</TableHead>
            <TableHead>Stato</TableHead><TableHead></TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {rows.length === 0 && (
              <TableRow><TableCell colSpan={isAdmin ? 8 : 7} className="py-10 text-center text-sm text-muted-foreground">
                Nessuna provvigione registrata.
              </TableCell></TableRow>
            )}
            {rows.map((r) => (
              <TableRow key={r.id} data-testid={`prov-row-${r.id}`}>
                <TableCell><Link to={isAdmin ? `/admin/preventivi/${r.id}` : `/preventivi/${r.id}`} className="font-medium text-terracotta">#{r.id.slice(-6).toUpperCase()}</Link>
                  <div className="text-xs text-muted-foreground">{PRODUCT_NAMES[r.product_code] || r.product_code}</div></TableCell>
                {isAdmin && <TableCell className="text-sm">{r.partner || "—"}<div className="text-xs text-muted-foreground">{r.codice_collab}</div></TableCell>}
                <TableCell className="text-sm text-forest">{r.contraente || "—"}</TableCell>
                <TableCell className="text-sm">{r.numero_proposta || "—"}</TableCell>
                <TableCell className="text-sm">{r.mese || "—"}</TableCell>
                <TableCell className="text-right font-semibold text-forest">{r.lordo != null ? eur(r.lordo) : "—"}</TableCell>
                <TableCell><span className={`rounded-full px-2.5 py-0.5 text-xs font-medium ${(STATO[r.stato] || {}).cls || "bg-slate-100 text-slate-600"}`}>{(STATO[r.stato] || {}).label || "—"}</span></TableCell>
                <TableCell className="text-right">
                  {!isAdmin && r.stato === "maturata" && (
                    <Button size="sm" variant="outline" data-testid={`prov-fattura-${r.id}`} onClick={() => segnaFattura(r)} disabled={busy === r.id} className="h-8 gap-1.5 border-forest/30 text-forest">
                      {busy === r.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <FileText className="h-3.5 w-3.5" />} Segna fattura emessa
                    </Button>
                  )}
                  {isAdmin && r.stato === "fatturata" && (
                    <Button size="sm" data-testid={`prov-liquida-${r.id}`} onClick={() => liquida(r)} disabled={busy === r.id} className="h-8 gap-1.5 bg-emerald-600 hover:bg-emerald-700">
                      {busy === r.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <BadgeEuro className="h-3.5 w-3.5" />} Registra bonifico
                    </Button>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
}
